import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card/Card';
import { Input } from '@/components/ui/Input/Input';
import { Select } from '@/components/ui/Select/Select';
import { Switch } from '@/components/ui/Switch/Switch';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { Alert } from '@/components/ui/Alert/Alert';
import { HStack, VStack } from '@/components/ui/Stack';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { SettingsService } from '@/services/SettingsService';
import { Mail, Send, Loader2 } from 'lucide-react';

const PROVIDER_OPTIONS = [
  { value: 'brevo', label: 'Brevo (Transactional API)' },
  { value: 'smtp', label: 'SMTP' },
  { value: 'console', label: 'Console (Development)' },
];

export function MailSettingsCard() {
  const queryClient = useQueryClient();
  const { success, error: notifyError } = useNotificationStore();
  const user = useAuthStore((s) => s.user);
  const service = new SettingsService();

  const [provider, setProvider] = useState<string>('');
  const [brevoApiKey, setBrevoApiKey] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [testEmail, setTestEmail] = useState('hello@rocket-routine.com');

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin-settings', 'mail'],
    queryFn: () => service.getMailSettings(),
  });

  // Populate form from fetched settings
  useEffect(() => {
    if (settings) {
      setProvider(settings.provider || '');
      setBrevoApiKey(''); // Don't show masked value as editable
      setSmtpHost(settings.smtp_host || '');
      setSmtpPort(settings.smtp_port != null ? String(settings.smtp_port) : '587');
      setSmtpSecure(settings.smtp_secure ?? false);
      setSmtpUser(settings.smtp_user || '');
      setSmtpPass(''); // Don't show masked value as editable
      setFromEmail(settings.from_email || '');
      setFromName(settings.from_name || '');
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (data: Parameters<typeof service.updateMailSettings>[0]) =>
      service.updateMailSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings', 'mail'] });
      success('Mail settings saved');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to save mail settings');
    },
  });

  const testMutation = useMutation({
    mutationFn: (email: string) => service.testMail(email),
    onSuccess: (data) => {
      success(data.message || 'Test email sent');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to send test email');
    },
  });

  const handleSave = () => {
    const data: any = { provider, from_email: fromEmail, from_name: fromName };

    if (provider === 'brevo') {
      // Only include api_key if a new value was entered
      if (brevoApiKey) data.brevo_api_key = brevoApiKey;
      else if (!settings?.brevo_api_key) data.brevo_api_key = ''; // Force validation error
    }

    if (provider === 'smtp') {
      data.smtp_host = smtpHost;
      data.smtp_port = parseInt(smtpPort, 10) || 587;
      data.smtp_secure = smtpSecure;
      data.smtp_user = smtpUser;
      if (smtpPass) data.smtp_pass = smtpPass;
    }

    saveMutation.mutate(data);
  };

  const handleTest = () => {
    if (!testEmail.trim()) {
      notifyError('Please enter a recipient email address');
      return;
    }
    testMutation.mutate(testEmail.trim());
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <HStack spacing={2} className="items-center py-4 justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span className="text-sm text-muted-foreground">Loading mail settings...</span>
          </HStack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <HStack spacing={2} className="items-center">
          <Mail className="w-5 h-5 text-primary-600" />
          <VStack spacing={0} className="flex-1">
            <HStack spacing={2} className="items-center">
              <CardTitle>Mail Configuration</CardTitle>
              {settings?.configured ? (
                <Badge variant="success">Configured</Badge>
              ) : (
                <Badge variant="secondary">Not Configured</Badge>
              )}
            </HStack>
            <CardDescription>
              Configure how the platform sends transactional emails (password resets, invitations, notifications).
            </CardDescription>
          </VStack>
        </HStack>
      </CardHeader>

      <CardContent>
        <VStack spacing={4}>
          <Select
            label="Email Provider"
            value={provider}
            onValueChange={setProvider}
            options={PROVIDER_OPTIONS}
            placeholder="Select a provider..."
          />

          {provider === 'brevo' && (
            <>
              <Input
                label="Brevo API Key"
                type="password"
                placeholder={settings?.brevo_api_key ? `Current: ${settings.brevo_api_key}` : 'Enter API key...'}
                value={brevoApiKey}
                onChange={(e) => setBrevoApiKey(e.target.value)}
                hint="Leave blank to keep the existing key"
              />
              <Input
                label="From Email"
                type="email"
                placeholder="noreply@example.com"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
              />
              <Input
                label="From Name"
                placeholder="My App"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
              />
            </>
          )}

          {provider === 'smtp' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="SMTP Host"
                  placeholder="smtp.example.com"
                  value={smtpHost}
                  onChange={(e) => setSmtpHost(e.target.value)}
                />
                <Input
                  label="SMTP Port"
                  type="number"
                  placeholder="587"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(e.target.value)}
                />
              </div>
              <Switch
                label="Use TLS/SSL"
                checked={smtpSecure}
                onChange={(e) => setSmtpSecure(e.target.checked)}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="SMTP Username"
                  placeholder="user@example.com"
                  value={smtpUser}
                  onChange={(e) => setSmtpUser(e.target.value)}
                />
                <Input
                  label="SMTP Password"
                  type="password"
                  placeholder={settings?.smtp_pass ? `Current: ${settings.smtp_pass}` : 'Enter password...'}
                  value={smtpPass}
                  onChange={(e) => setSmtpPass(e.target.value)}
                  hint="Leave blank to keep the existing password"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="From Email"
                  type="email"
                  placeholder="noreply@example.com"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                />
                <Input
                  label="From Name"
                  placeholder="My App"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                />
              </div>
            </>
          )}

          {provider === 'console' && (
            <Alert variant="info" title="Console Provider">
              Emails will be logged to the server console instead of being sent. This is intended for development and testing only.
            </Alert>
          )}

          {provider && (
            <HStack spacing={3} className="pt-2">
              <Button
                onClick={handleSave}
                isLoading={saveMutation.isPending}
                disabled={!provider}
              >
                Save Mail Settings
              </Button>
              <Input
                type="email"
                placeholder="Recipient email..."
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="w-56"
              />
              <Button
                variant="outline"
                onClick={handleTest}
                isLoading={testMutation.isPending}
                disabled={!settings?.configured}
                leftIcon={<Send className="w-4 h-4" />}
              >
                Send Test
              </Button>
            </HStack>
          )}
        </VStack>
      </CardContent>
    </Card>
  );
}
